
import React, { useState, useEffect } from "react";
import { CommissionsGrid } from "@/components/commissions/CommissionsGrid";
import { Navigation } from "@/components/Navigation";
import { 
  Dialog, 
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Wallet, Search, Smartphone, Info } from "lucide-react";
import { useEthereumWallet } from "@/hooks/commission/use-ethereum-wallet";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { connectToMetaMaskMobile, checkMobileWalletReturn } from "@/services/commission/mobileWalletService";

const Commissions = () => {
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { 
    isConnected, 
    address, 
    balance, 
    isConnecting, 
    connectWallet, 
    disconnectWallet,
    error
  } = useEthereumWallet();
  const isMobile = useIsMobile();

  // State to control active tab
  const [activeTab, setActiveTab] = useState("explore");

  // Verificar se o usuário está retornando do app MetaMask
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (checkMobileWalletReturn() && !isConnected) {
        // Tentar conectar a carteira, pois o usuário pode ter aprovado no app
        try {
          await connectWallet();
          toast({
            title: "Sucesso",
            description: "Carteira conectada com sucesso!",
            variant: "default",
          });
        } catch (err) {
          console.error("Erro ao reconectar carteira:", err);
          toast({
            title: "Erro",
            description: "Não foi possível conectar a carteira",
            variant: "destructive",
          });
        }
      }
    };

    checkWalletConnection();
  }, [connectWallet, isConnected]);

  const handleConnectWallet = async () => {
    // Se estiver em dispositivo móvel, tentar usar o app MetaMask
    if (isMobile) {
      const openedMobileApp = await connectToMetaMaskMobile();
      if (openedMobileApp) {
        // A página será redirecionada para o app, não precisamos fazer mais nada aqui
        return;
      }
    }
    
    // Se não estiver em dispositivo móvel ou se a tentativa de abrir o app falhou,
    // continuar com o fluxo normal de conexão
    await connectWallet();
    setWalletModalOpen(false);
  };

  const handleDisconnectWallet = () => {
    disconnectWallet();
    setWalletModalOpen(false);
  };

  const formatAddress = (address: string) => {
    if (!address) return "";
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="min-h-screen bg-background pt-12">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between mb-6">
          <div className="w-full md:w-1/2 mb-4 md:mb-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input 
                placeholder="Pesquisar comissões..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div className={isMobile ? "w-full" : ""}>
            {isConnected ? (
              <Button variant="wallet" onClick={() => setWalletModalOpen(true)} className={isMobile ? "w-full" : ""}>
                <Wallet className="mr-2 h-4 w-4" />
                {formatAddress(address || "")}
                {balance && <span className="ml-2 text-xs opacity-75">{parseFloat(balance).toFixed(4)} ETH</span>}
              </Button>
            ) : (
              <Button variant="wallet" onClick={() => setWalletModalOpen(true)} className={isMobile ? "w-full" : ""}>
                <Wallet className="mr-2 h-4 w-4" />
                Conectar Carteira
              </Button>
            )}
          </div>
        </div>
        
        <CommissionsGrid searchFilter={searchQuery} activeTab={activeTab} setActiveTab={setActiveTab} />
        
        <Dialog open={walletModalOpen} onOpenChange={setWalletModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isConnected ? "Carteira Conectada" : "Conectar Carteira"}</DialogTitle>
              <DialogDescription>
                {isConnected 
                  ? "Sua carteira está conectada. Você pode desconectá-la a qualquer momento."
                  : "Conecte sua carteira para comprar ou vender comissões usando criptomoedas."
                }
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-md mb-4 text-sm">
                  {error}
                </div>
              )}
              
              {isConnected ? (
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-1">Endereço</p>
                    <p className="font-mono text-sm break-all">{address}</p>
                  </div>
                  
                  {balance && (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm font-medium mb-1">Saldo</p>
                      <p className="text-lg font-semibold">{parseFloat(balance).toFixed(6)} ETH</p>
                    </div>
                  )}
                  
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={handleDisconnectWallet}
                  >
                    Desconectar Carteira
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Button 
                    variant="metamask" 
                    className="w-full flex items-center justify-center h-16 text-lg" 
                    onClick={handleConnectWallet}
                    disabled={isConnecting}
                  >
                    <img 
                      src="https://jzmgcrxhpzztzljikyoa.supabase.co/storage/v1/object/public/pins/pins/metamask-icon.svg" 
                      alt="MetaMask" 
                      className="w-8 h-8 mr-3"
                    />
                    {isConnecting ? "Conectando..." : (isMobile ? "Abrir App MetaMask" : "Conectar com MetaMask")}
                  </Button>
                  
                  {isMobile && (
                    <div className="flex flex-col space-y-2 mt-2">
                      <div className="flex items-center justify-center text-xs text-muted-foreground">
                        <Smartphone className="h-3 w-3 mr-1" />
                        <span>Abre o aplicativo MetaMask no seu celular</span>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-amber-800 dark:text-amber-300 text-sm flex items-start">
                        <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                        <p>
                          Para melhor experiência e compatibilidade com MetaMask, recomendamos usar um navegador de computador. 
                          A conexão em dispositivos móveis pode apresentar limitações.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Ou instale a <a href="https://metamask.io/download" target="_blank" rel="noopener noreferrer" className="underline">extensão MetaMask</a> se você ainda não tem uma carteira
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Commissions;
